import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import VendrLayout from '../VendrLayout';
import { useClientProfile } from '../context/ClientProfileContext';
import { useVendrProducts } from '../context/VendrProductsContext';
import { uploadVendrFile } from '../utils/vendrStorage';
import {
  downloadProductTemplateCsv,
  downloadProductTemplateXlsx,
  parseAndValidateProductImportFile,
} from '../utils/productImport';
import {
  fetchActiveProductImportTemplate,
  downloadActiveTemplateToDisk,
} from '../../utils/productImportTemplateService';
import FormSection from '../components/FormSection';
import FormField from '../components/FormField';
import { useAmazonRegistry } from '../context/AmazonBrandRegistryContext';
import './VendrProductForm.css';

const emptyProduct = {
  sku_number: '',
  product_name: '',
  product_description: '',
  volume: null,
  alcohol_abv: '',
  commodity_code: '',
  country_of_manufacture: '',
  shelf_life: '',
  item_type: '',
  quantity_per_case: null,
  bottle_material: '',
  can_material: '',
  cases_per_pallet: null,
  net_case_weight: null,
  gross_case_weight: null,
  case_height_mm: null,
  case_length_mm: null,
  case_width_mm: null,
  rrp_per_bottle_gbp: null,
  rrp_per_case_b2b_gbp: null,
  cost_price_per_bottle_gbp: null,
  cost_price_per_case_gbp: null,
  currency_rrp_bottle: 'GBP',
  currency_rrp_case: 'GBP',
  currency_cost_bottle: 'GBP',
  currency_cost_case: 'GBP',
  label_image_front_path: null,
  label_image_back_path: null,
  ean_barcode_closeup_path: null,
  brand_guidelines_path: null,
  brand_book_path: null,
  status: 'draft',
};

const NUM_FIELDS = [
  'volume', 'quantity_per_case', 'cases_per_pallet', 'net_case_weight', 'gross_case_weight',
  'case_height_mm', 'case_length_mm', 'case_width_mm',
  'rrp_per_bottle_gbp', 'rrp_per_case_b2b_gbp', 'cost_price_per_bottle_gbp', 'cost_price_per_case_gbp',
];

const FILE_ACCEPT = 'image/jpeg,image/png,application/pdf';

const ITEM_TYPE_OPTIONS = [
  { value: 'bottle', label: 'Bottle' },
  { value: 'can', label: 'Can' },
  { value: 'item', label: 'Item' },
];

const CURRENCY_OPTIONS = [
  { value: 'GBP', label: '£ GBP' },
  { value: 'USD', label: '$ USD' },
  { value: 'EUR', label: '€ EUR' },
  { value: 'CHF', label: 'CHF' },
  { value: 'AUD', label: 'A$ AUD' },
  { value: 'CAD', label: 'C$ CAD' },
];

const VendrProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, isSetupComplete, loading: profileLoading } = useClientProfile();
  const { getProductById, createProduct, updateProduct, loadProducts, bulkImportProducts } = useVendrProducts();
  const { isAmazonEnabled, isAmazonSubmitted } = useAmazonRegistry();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(emptyProduct);
  const [files, setFiles] = useState({
    label_image_front: null,
    label_image_back: null,
    ean_barcode_closeup: null,
    brand_guidelines: null,
    brand_book: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [entryMode, setEntryMode] = useState('manual');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const importInputRef = useRef(null);
  const [officialTemplate, setOfficialTemplate] = useState(null);
  const [officialTemplateLoading, setOfficialTemplateLoading] = useState(false);

  const product = isEdit ? getProductById(id) : null;

  const loadOfficialTemplate = useCallback(async () => {
    setOfficialTemplateLoading(true);
    const { data, error } = await fetchActiveProductImportTemplate();
    setOfficialTemplateLoading(false);
    if (!error && data?.id) {
      setOfficialTemplate(data);
    } else {
      setOfficialTemplate(null);
    }
  }, []);

  useEffect(() => {
    if (!isEdit) {
      loadOfficialTemplate();
    }
  }, [isEdit, loadOfficialTemplate]);

  useEffect(() => {
    if (!isEdit && entryMode === 'import') {
      loadOfficialTemplate();
    }
  }, [isEdit, entryMode, loadOfficialTemplate]);

  useEffect(() => {
    if (product) {
      setForm({
        sku_number: product.sku_number ?? '',
        product_name: product.product_name ?? '',
        product_description: product.product_description ?? '',
        volume: product.volume != null && product.volume !== '' ? Number(product.volume) : null,
        alcohol_abv: product.alcohol_abv ?? '',
        commodity_code: product.commodity_code ?? '',
        country_of_manufacture: product.country_of_manufacture ?? '',
        shelf_life: product.shelf_life ?? '',
        item_type: product.item_type ?? '',
        quantity_per_case: product.quantity_per_case ?? null,
        bottle_material: product.bottle_material ?? '',
        can_material: product.can_material ?? '',
        cases_per_pallet: product.cases_per_pallet ?? null,
        net_case_weight: product.net_case_weight ?? null,
        gross_case_weight: product.gross_case_weight ?? null,
        case_height_mm: product.case_height_mm ?? null,
        case_length_mm: product.case_length_mm ?? null,
        case_width_mm: product.case_width_mm ?? null,
        rrp_per_bottle_gbp: product.rrp_per_bottle_gbp ?? null,
        rrp_per_case_b2b_gbp: product.rrp_per_case_b2b_gbp ?? null,
        cost_price_per_bottle_gbp: product.cost_price_per_bottle_gbp ?? null,
        cost_price_per_case_gbp: product.cost_price_per_case_gbp ?? null,
        currency_rrp_bottle: product.currency_rrp_bottle ?? product.currency ?? 'GBP',
        currency_rrp_case: product.currency_rrp_case ?? product.currency ?? 'GBP',
        currency_cost_bottle: product.currency_cost_bottle ?? product.currency ?? 'GBP',
        currency_cost_case: product.currency_cost_case ?? product.currency ?? 'GBP',
        label_image_front_path: product.label_image_front_path ?? null,
        label_image_back_path: product.label_image_back_path ?? null,
        ean_barcode_closeup_path: product.ean_barcode_closeup_path ?? null,
        brand_guidelines_path: product.brand_guidelines_path ?? null,
        brand_book_path: product.brand_book_path ?? null,
        status: product.status ?? 'draft',
      });
    }
  }, [product, isEdit]);

  if (profileLoading) {
    return (
      <VendrLayout>
        <div className="vendr-form-loading">Loading...</div>
      </VendrLayout>
    );
  }

  if (!isSetupComplete) {
    navigate('/vendr/setup', { replace: true });
    return null;
  }

  if (isAmazonEnabled && !isAmazonSubmitted) {
    navigate('/vendr/amazon-registry', { replace: true });
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: NUM_FIELDS.includes(name) ? (value === '' ? null : Number(value)) : value,
    }));
  };

  const handleFile = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file || null }));
  };

  const getSavedFileName = (pathKey) => {
    const path = form[pathKey] ?? product?.[pathKey];
    if (!path || typeof path !== 'string') return false;
    const name = path.split('/').pop() || path;
    return name;
  };

  const uploadFilesForProduct = async (clientProfileId, productId) => {
    const paths = {};
    const keys = [
      'label_image_front', 'label_image_back', 'ean_barcode_closeup',
      'brand_guidelines', 'brand_book',
    ];
    const pathKeys = {
      label_image_front: 'label_image_front_path',
      label_image_back: 'label_image_back_path',
      ean_barcode_closeup: 'ean_barcode_closeup_path',
      brand_guidelines: 'brand_guidelines_path',
      brand_book: 'brand_book_path',
    };
    for (const key of keys) {
      const file = files[key];
      if (!file) continue;
      const customName = key + '_' + Date.now() + '.' + (file.name.split('.').pop() || 'bin');
      const result = await uploadVendrFile(file, clientProfileId, productId, customName);
      if (result.success) paths[pathKeys[key]] = result.path;
    }
    return paths;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError('');
    if (!profile?.id) {
      setError('Company profile is missing. Please complete company setup first.');
      return;
    }
    if (!form.sku_number?.trim() || !form.product_name?.trim()) {
      setError('SKU number and product name are required.');
      return;
    }
    if (!form.commodity_code.trim()) {
      setError('Commodity / HS code is required.');
      return;
    }
    if (!form.country_of_manufacture.trim()) {
      setError('Country of manufacture is required.');
      return;
    }
    if (form.volume == null || form.volume === '') {
      setError('Volume (ml) is required.');
      return;
    }
    if (!isEdit) {
      const hasLabelFront = files.label_image_front || form.label_image_front_path;
      const hasLabelBack = files.label_image_back || form.label_image_back_path;
      const hasEan = files.ean_barcode_closeup || form.ean_barcode_closeup_path;
      if (!hasLabelFront || !hasLabelBack || !hasEan) {
        setError('Label image (front), label image (back), and EAN barcode close-up are required.');
        return;
      }
    }
    setSaving(true);
    const timeoutMs = 60000;
    const timeoutId = setTimeout(() => {
      setSaving(false);
      setError('Request timed out. Check your connection and try again.');
    }, timeoutMs);

    try {
      const payload = {
        sku_number: form.sku_number.trim(),
        product_name: form.product_name.trim(),
        product_description: form.product_description?.trim() || null,
        volume: form.volume != null && form.volume !== '' ? String(form.volume) : null,
        alcohol_abv: form.alcohol_abv?.trim() || null,
        commodity_code: form.commodity_code.trim(),
        country_of_manufacture: form.country_of_manufacture.trim(),
        shelf_life: form.shelf_life?.trim() || null,
        item_type: form.item_type || null,
        pack_size: null,
        quantity_per_case: form.quantity_per_case,
        bottle_material: form.bottle_material?.trim() || null,
        can_material: null,
        cases_per_pallet: form.cases_per_pallet,
        net_case_weight: form.net_case_weight,
        gross_case_weight: form.gross_case_weight,
        case_height_mm: form.case_height_mm,
        case_length_mm: form.case_length_mm,
        case_width_mm: form.case_width_mm,
        rrp_per_bottle_gbp: form.rrp_per_bottle_gbp,
        rrp_per_case_b2b_gbp: form.rrp_per_case_b2b_gbp,
        cost_price_per_bottle_gbp: form.cost_price_per_bottle_gbp,
        cost_price_per_case_gbp: form.cost_price_per_case_gbp,
        currency_rrp_bottle: form.currency_rrp_bottle || 'GBP',
        currency_rrp_case: form.currency_rrp_case || 'GBP',
        currency_cost_bottle: form.currency_cost_bottle || 'GBP',
        currency_cost_case: form.currency_cost_case || 'GBP',
        status: 'submitted',
        // When editing, keep existing file paths if form has none (files aren't re-loaded into the file inputs)
        label_image_front_path: form.label_image_front_path ?? product?.label_image_front_path ?? null,
        label_image_back_path: form.label_image_back_path ?? product?.label_image_back_path ?? null,
        ean_barcode_closeup_path: form.ean_barcode_closeup_path ?? product?.ean_barcode_closeup_path ?? null,
        brand_guidelines_path: form.brand_guidelines_path ?? product?.brand_guidelines_path ?? null,
        brand_book_path: form.brand_book_path ?? product?.brand_book_path ?? null,
      };

      let productId;
      if (isEdit) {
        const result = await updateProduct(id, payload);
        if (!result.success) {
          setError(result.error || 'Failed to update product');
          setSaving(false);
          clearTimeout(timeoutId);
          return;
        }
        productId = id;
      } else {
        const result = await createProduct(payload);
        if (!result.success) {
          setError(result.error || 'Failed to create product');
          setSaving(false);
          clearTimeout(timeoutId);
          return;
        }
        if (!result.data?.id) {
          setError('Server did not return the new product. Please try again.');
          setSaving(false);
          clearTimeout(timeoutId);
          return;
        }
        productId = result.data.id;
      }

      const hasAnyFile = Object.values(files).some(Boolean);
      if (hasAnyFile && profile?.id) {
        const uploadedPaths = await uploadFilesForProduct(profile.id, productId);
        if (Object.keys(uploadedPaths).length > 0) {
          const updateResult = await updateProduct(productId, uploadedPaths);
          if (!updateResult.success) {
            setError(updateResult.error || 'Product saved but file upload failed to update.');
            setSaving(false);
            clearTimeout(timeoutId);
            return;
          }
        }
      }

      await loadProducts();
      clearTimeout(timeoutId);
      navigate('/vendr/products');
    } catch (err) {
      setError(err?.message || 'Something went wrong');
    } finally {
      clearTimeout(timeoutId);
      setSaving(false);
    }
  };

  const handleDownloadCsvTemplate = () => {
    downloadProductTemplateCsv();
  };

  const handleDownloadXlsxTemplate = () => {
    downloadProductTemplateXlsx();
  };

  const handleDownloadOfficialTemplate = async () => {
    setImportError('');
    const result = await downloadActiveTemplateToDisk();
    if (!result.success) {
      setImportError(result.error || 'Could not download the official template.');
    }
  };

  const openImportPicker = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportError('');
    setImportResult(null);

    try {
      const parsed = await parseAndValidateProductImportFile(file);
      const validationErrors = parsed.errors || [];

      if (parsed.validRows.length === 0) {
        setImportResult({
          insertedCount: 0,
          updatedCount: 0,
          failedRows: validationErrors,
        });
        return;
      }

      const result = await bulkImportProducts(parsed.validRows);
      if (!result.success) {
        setImportError(result.error || 'Import failed');
        return;
      }

      const dbFailedRows = result.data?.failedRows || [];
      setImportResult({
        insertedCount: result.data?.insertedCount || 0,
        updatedCount: result.data?.updatedCount || 0,
        failedRows: [...validationErrors, ...dbFailedRows],
      });
      await loadProducts();
    } catch (err) {
      setImportError(err?.message || 'Import failed');
    } finally {
      setImporting(false);
      if (event.target) event.target.value = '';
    }
  };

  const showManualForm = isEdit || entryMode === 'manual';

  return (
    <VendrLayout>
      <div className="vendr-product-form">
        <div className="vendr-product-form-header">
          <div className="vendr-product-form-header-title">
            <h1>{isEdit ? 'Edit product' : 'Add product'}</h1>
            {!isEdit && (
              <p className="vendr-product-form-intro">
                Add one product manually, or switch to spreadsheet import for bulk upload.
              </p>
            )}
          </div>
          <button
            type="button"
            className="vendr-form-back"
            onClick={() => navigate('/vendr/products')}
          >
            Back to list
          </button>
        </div>

        {!isEdit && (
          <div className="vendr-product-entry-mode">
            <button
              type="button"
              className={`vendr-entry-mode-btn${entryMode === 'manual' ? ' vendr-entry-mode-btn--active' : ''}`}
              onClick={() => setEntryMode('manual')}
            >
              Manual entry
            </button>
            <button
              type="button"
              className={`vendr-entry-mode-btn${entryMode === 'import' ? ' vendr-entry-mode-btn--active' : ''}`}
              onClick={() => setEntryMode('import')}
            >
              Spreadsheet import
            </button>
          </div>
        )}

        {showManualForm ? (
          <form onSubmit={handleSubmit} className="vendr-form" noValidate>
          {error && <div className="vendr-form-error">{error}</div>}

          <FormSection title="Product / SKU details">
            <FormField label="SKU number" name="sku_number" value={form.sku_number} onChange={handleChange} required />
            <FormField label="Product name" name="product_name" value={form.product_name} onChange={handleChange} required />
            <FormField label="Product description" name="product_description" value={form.product_description} onChange={handleChange} inputType="textarea" rows={3} optional fullWidth />
            <FormField label="Volume (ml)" name="volume" value={form.volume} onChange={handleChange} inputType="number" required />
            <FormField label="Alcohol ABV" name="alcohol_abv" value={form.alcohol_abv} onChange={handleChange} optional />
            <FormField label="Commodity / HS code" name="commodity_code" value={form.commodity_code} onChange={handleChange} required />
            <FormField label="Country of manufacture" name="country_of_manufacture" value={form.country_of_manufacture} onChange={handleChange} required />
            <FormField label="Shelf life" name="shelf_life" value={form.shelf_life} onChange={handleChange} optional />
          </FormSection>

          <FormSection title="Packaging & logistics">
            <FormField label="Item type" name="item_type" value={form.item_type} onChange={handleChange} inputType="select" selectOptions={ITEM_TYPE_OPTIONS} optional />
            <FormField label="Quantity per case" name="quantity_per_case" value={form.quantity_per_case} onChange={handleChange} inputType="number" optional />
            <FormField label="Item material" name="bottle_material" value={form.bottle_material} onChange={handleChange} optional />
            <FormField label="Cases per pallet" name="cases_per_pallet" value={form.cases_per_pallet} onChange={handleChange} inputType="number" optional />
            <FormField label="Net case weight" name="net_case_weight" value={form.net_case_weight} onChange={handleChange} inputType="number" step="any" unitHint="(kg)" optional />
            <FormField label="Gross case weight" name="gross_case_weight" value={form.gross_case_weight} onChange={handleChange} inputType="number" step="any" unitHint="(kg)" optional />
            <FormField label="Case height" name="case_height_mm" value={form.case_height_mm} onChange={handleChange} inputType="number" unitHint="(mm)" optional />
            <FormField label="Case length" name="case_length_mm" value={form.case_length_mm} onChange={handleChange} inputType="number" unitHint="(mm)" optional />
            <FormField label="Case width" name="case_width_mm" value={form.case_width_mm} onChange={handleChange} inputType="number" unitHint="(mm)" optional />
          </FormSection>

          <FormSection title="Pricing">
            <div className="vendr-form-price-row">
              <label htmlFor="field-rrp_per_bottle_gbp" className="form-field-label">RRP per bottle <span className="form-field-optional">(optional)</span></label>
              <div className="vendr-form-price-input-wrap">
                <input
                  id="field-rrp_per_bottle_gbp"
                  type="number"
                  step="any"
                  name="rrp_per_bottle_gbp"
                  value={form.rrp_per_bottle_gbp === null || form.rrp_per_bottle_gbp === undefined ? '' : form.rrp_per_bottle_gbp}
                  onChange={handleChange}
                  className="form-field-input vendr-form-price-input"
                />
                <select
                  aria-label="Currency for RRP per bottle"
                  className="vendr-form-currency-select"
                  value={form.currency_rrp_bottle || 'GBP'}
                  onChange={(e) => setForm((prev) => ({ ...prev, currency_rrp_bottle: e.target.value }))}
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="vendr-form-price-row">
              <label htmlFor="field-rrp_per_case_b2b_gbp" className="form-field-label">RRP per case B2B <span className="form-field-optional">(optional)</span></label>
              <div className="vendr-form-price-input-wrap">
                <input
                  id="field-rrp_per_case_b2b_gbp"
                  type="number"
                  step="any"
                  name="rrp_per_case_b2b_gbp"
                  value={form.rrp_per_case_b2b_gbp === null || form.rrp_per_case_b2b_gbp === undefined ? '' : form.rrp_per_case_b2b_gbp}
                  onChange={handleChange}
                  className="form-field-input vendr-form-price-input"
                />
                <select
                  aria-label="Currency for RRP per case"
                  className="vendr-form-currency-select"
                  value={form.currency_rrp_case || 'GBP'}
                  onChange={(e) => setForm((prev) => ({ ...prev, currency_rrp_case: e.target.value }))}
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="vendr-form-price-row">
              <label htmlFor="field-cost_price_per_bottle_gbp" className="form-field-label">Cost per bottle <span className="form-field-optional">(optional)</span></label>
              <div className="vendr-form-price-input-wrap">
                <input
                  id="field-cost_price_per_bottle_gbp"
                  type="number"
                  step="any"
                  name="cost_price_per_bottle_gbp"
                  value={form.cost_price_per_bottle_gbp === null || form.cost_price_per_bottle_gbp === undefined ? '' : form.cost_price_per_bottle_gbp}
                  onChange={handleChange}
                  className="form-field-input vendr-form-price-input"
                />
                <select
                  aria-label="Currency for cost per bottle"
                  className="vendr-form-currency-select"
                  value={form.currency_cost_bottle || 'GBP'}
                  onChange={(e) => setForm((prev) => ({ ...prev, currency_cost_bottle: e.target.value }))}
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="vendr-form-price-row">
              <label htmlFor="field-cost_price_per_case_gbp" className="form-field-label">Cost per case <span className="form-field-optional">(optional)</span></label>
              <div className="vendr-form-price-input-wrap">
                <input
                  id="field-cost_price_per_case_gbp"
                  type="number"
                  step="any"
                  name="cost_price_per_case_gbp"
                  value={form.cost_price_per_case_gbp === null || form.cost_price_per_case_gbp === undefined ? '' : form.cost_price_per_case_gbp}
                  onChange={handleChange}
                  className="form-field-input vendr-form-price-input"
                />
                <select
                  aria-label="Currency for cost per case"
                  className="vendr-form-currency-select"
                  value={form.currency_cost_case || 'GBP'}
                  onChange={(e) => setForm((prev) => ({ ...prev, currency_cost_case: e.target.value }))}
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Images & files (JPG, PNG, PDF)" className="vendr-form-files">
            <FormField label="Label image (front)" name="label_image_front" inputType="file" accept={FILE_ACCEPT} fileSaved={getSavedFileName('label_image_front_path')} onChange={(e) => handleFile('label_image_front', e.target.files?.[0])} required />
            <FormField label="Label image (back)" name="label_image_back" inputType="file" accept={FILE_ACCEPT} fileSaved={getSavedFileName('label_image_back_path')} onChange={(e) => handleFile('label_image_back', e.target.files?.[0])} required />
            <FormField label="EAN barcode close-up" name="ean_barcode_closeup" inputType="file" accept={FILE_ACCEPT} fileSaved={getSavedFileName('ean_barcode_closeup_path')} onChange={(e) => handleFile('ean_barcode_closeup', e.target.files?.[0])} required />
            <FormField label="Brand guidelines PDF" name="brand_guidelines" inputType="file" accept={FILE_ACCEPT} fileSaved={getSavedFileName('brand_guidelines_path')} onChange={(e) => handleFile('brand_guidelines', e.target.files?.[0])} optional />
            <FormField label="Brand book PDF" name="brand_book" inputType="file" accept={FILE_ACCEPT} fileSaved={getSavedFileName('brand_book_path')} onChange={(e) => handleFile('brand_book', e.target.files?.[0])} optional />
          </FormSection>

          <div className="vendr-form-actions">
            <button
              type="button"
              className="vendr-form-submit"
              disabled={saving}
              onClick={() => handleSubmit({ preventDefault: () => {} })}
            >
              {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Add product'}
            </button>
            <button type="button" className="vendr-form-cancel" onClick={() => navigate('/vendr/products')}>
              Cancel
            </button>
          </div>
          </form>
        ) : (
          <div className="vendr-import-panel">
            <h2>Import products from spreadsheet</h2>
            <p>
              Download a template, fill it in, then upload the CSV or XLSX file. Existing SKUs are updated; new SKUs are created.
            </p>
            {officialTemplateLoading && (
              <p className="vendr-import-official-hint">Checking for an official template…</p>
            )}
            {!officialTemplateLoading && officialTemplate && (
              <p className="vendr-import-official-hint">
                Official template: <strong>{officialTemplate.name}</strong> ({String(officialTemplate.file_type || '').toUpperCase()})
              </p>
            )}
            {!officialTemplateLoading && !officialTemplate && (
              <p className="vendr-import-official-hint">
                No official template is set yet. You can use the simple generated templates below, or ask your administrator to upload one under Import templates.
              </p>
            )}
            <div className="vendr-import-actions">
              {officialTemplate && (
                <button type="button" className="vendr-form-submit" onClick={handleDownloadOfficialTemplate}>
                  Download official template
                </button>
              )}
              <button type="button" className="vendr-form-cancel" onClick={handleDownloadCsvTemplate}>
                Download generated CSV
              </button>
              <button type="button" className="vendr-form-cancel" onClick={handleDownloadXlsxTemplate}>
                Download generated XLSX
              </button>
              <button type="button" className="vendr-form-submit" onClick={openImportPicker} disabled={importing}>
                {importing ? 'Importing...' : 'Import file'}
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleImportFile}
                className="vendr-import-hidden-input"
              />
            </div>

            {importError && <div className="vendr-form-error">{importError}</div>}

            {importResult && (
              <div className="vendr-import-result">
                <p><strong>Inserted:</strong> {importResult.insertedCount}</p>
                <p><strong>Updated:</strong> {importResult.updatedCount}</p>
                <p><strong>Failed rows:</strong> {importResult.failedRows.length}</p>
                {importResult.failedRows.length > 0 && (
                  <div className="vendr-import-errors">
                    {importResult.failedRows.slice(0, 25).map((rowError, idx) => (
                      <p key={`${rowError.row}-${rowError.field}-${idx}`}>
                        Row {rowError.row || '-'} [{rowError.field}]: {rowError.error}
                      </p>
                    ))}
                    {importResult.failedRows.length > 25 && (
                      <p>Showing first 25 errors. Please fix and re-import remaining rows.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </VendrLayout>
  );
};

export default VendrProductForm;
